// Publish revisions (2A): the editor's client for the publish and remind
// routes, plus the date wording zone 2 and the dialogs share.

import { api } from './api';
import type { Script, ScriptStatus } from '../../shared/types';
import type { UnpublishedKind } from '../../shared/publish';

/** What the admin GET says about the published copy. */
export type PublishInfo = Pick<
  Script,
  | 'published_round' | 'published_at' | 'published_version_id' | 'published_doc_version'
  | 'published_title' | 'published_note' | 'round_published_at' | 'last_client_feedback_at'
  | 'last_client_feedback_by' | 'last_reminded_at' | 'approved_at' | 'approved_by_name'
  | 'email_enabled' | 'unpublished'
>;

export interface PublishPreview {
  unpublished: UnpublishedKind;
  first_publish: boolean;
  changes: { changed: number; added: number; removed: number };
  current: { status: ScriptStatus; round: number | null; published_at: number | null };
  next_round: number;
  feedback: { at: number; by: string } | null;
  pending_suggestion: { author_name: string; submitted_at: number | null } | null;
  email: { available: boolean; default_checked: boolean; reason: 'no_client_email' | 'email_off' | null; to: string | null };
}

export interface PublishResponse {
  ok: true;
  status: ScriptStatus;
  published_round: number;
  published_at: number;
  published_version_id: string;
  published_doc_version: number;
  unpublished: UnpublishedKind;
  email: 'queued' | 'none';
}

export const publishApi = {
  preview: (id: string) => api<PublishPreview>(`/api/admin/scripts/${id}/publish/preview`),
  summary: (id: string) =>
    api<{ bullets: string[]; error?: string }>(`/api/admin/scripts/${id}/publish/summary`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  publish: (
    id: string,
    body: { expected_version: number; mode: 'round' | 'quiet'; target_round?: number; email?: boolean; bullets?: string[]; note?: string },
  ) =>
    api<PublishResponse>(`/api/admin/scripts/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  remind: (id: string) =>
    api<{ ok: true; last_reminded_at: number }>(`/api/admin/scripts/${id}/remind`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
};

const DAY_MS = 24 * 60 * 60 * 1000;

function clock(d: Date): string {
  const h = d.getHours();
  const m = d.getMinutes();
  const suffix = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hour}${suffix}` : `${hour}:${String(m).padStart(2, '0')}${suffix}`;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Zone 2 wording: "just now", "today 4:12pm", "Tue 4:12pm" within six days, else "27 Oct". */
export function shortWhen(at: number, now = Date.now()): string {
  if (now - at < 60_000) return 'just now';
  const d = new Date(at);
  const today = new Date(now);
  if (sameDay(d, today)) return `today ${clock(d)}`;
  if (now - at < 6 * DAY_MS) return `${d.toLocaleDateString('en-AU', { weekday: 'short' })} ${clock(d)}`;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

/** Long form for sentences: "Tue 27 Oct at 4:12pm". */
export function longWhen(at: number): string {
  const d = new Date(at);
  const date = d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '');
  return `${date} at ${clock(d)}`;
}

/** Date only: "Mon 2 Nov". */
export function dayWhen(at: number): string {
  return new Date(at).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '');
}

/** "today 10am" / "tomorrow 10am" / "Mon 10am", for reminder lines. */
export function relativeDay(at: number, now = Date.now()): string {
  const d = new Date(at);
  const today = new Date(now);
  const tomorrow = new Date(now + DAY_MS);
  if (sameDay(d, today)) return `today ${clock(d)}`;
  if (sameDay(d, tomorrow)) return `tomorrow ${clock(d)}`;
  return `${d.toLocaleDateString('en-AU', { weekday: 'short' })} ${clock(d)}`;
}

/** "4 rows changed, 1 added." from the block counts. */
export function changeSentence(changes: PublishPreview['changes']): string {
  const parts: string[] = [];
  const rows = (n: number) => `${n} ${n === 1 ? 'row' : 'rows'}`;
  if (changes.changed) parts.push(`${rows(changes.changed)} changed`);
  if (changes.added) parts.push(parts.length ? `${changes.added} added` : `${rows(changes.added)} added`);
  if (changes.removed) parts.push(parts.length ? `${changes.removed} removed` : `${rows(changes.removed)} removed`);
  return parts.length ? `${parts.join(', ')}.` : 'No changes to what the client reads.';
}
